import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Clock,
  CheckCircle,
  Circle,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  Save,
  Send,
  Flag,
  BookOpen,
  Timer,
  Award,
  RotateCcw,
  Eye,
} from "lucide-react";
import {
  Quiz,
  QuizQuestion,
  QuizAttempt,
  QuizSettings,
} from "../../types/elearning";
import apiClient from "../../services/api";
import { useQuizTimer } from "../../hooks/useQuizTimer";

interface QuizState {
  quiz: Quiz | null;
  questions: QuizQuestion[];
  currentQuestionIndex: number;
  answers: Record<string, any>;
  isSubmitted: boolean;
  attempt: QuizAttempt | null;
  loading: boolean;
  error: string | null;
  flaggedQuestions: Set<string>;
}

interface QuizTimerProps {
  timeRemaining: number;
  onTimeUp: () => void;
  isActive: boolean;
}

const QuizTimer: React.FC<QuizTimerProps> = ({
  timeRemaining,
  onTimeUp,
  isActive,
}) => {
  const minutes = Math.floor(timeRemaining / 60);
  const seconds = timeRemaining % 60;

  const isWarning = timeRemaining <= 300; // 5 minutes
  const isCritical = timeRemaining <= 60; // 1 minute

  useEffect(() => {
    if (timeRemaining <= 0 && isActive) {
      onTimeUp();
    }
  }, [timeRemaining, isActive, onTimeUp]);

  return (
    <div
      className={`flex items-center space-x-2 px-3 py-2 rounded-lg font-mono text-sm ${
        isCritical
          ? "bg-red-100 text-red-800 border border-red-300"
          : isWarning
          ? "bg-yellow-100 text-yellow-800 border border-yellow-300"
          : "bg-blue-100 text-blue-800 border border-blue-300"
      }`}
    >
      <Timer className={`w-4 h-4 ${isCritical ? "animate-pulse" : ""}`} />
      <span>
        {minutes.toString().padStart(2, "0")}:
        {seconds.toString().padStart(2, "0")}
      </span>
    </div>
  );
};

interface QuestionRendererProps {
  question: QuizQuestion;
  answer: any;
  onAnswerChange: (answer: any) => void;
  isReviewMode?: boolean;
  showCorrectAnswer?: boolean;
}

const QuestionRenderer: React.FC<QuestionRendererProps> = ({
  question,
  answer,
  onAnswerChange,
  isReviewMode = false,
  showCorrectAnswer = false,
}) => {
  const renderMultipleChoice = () => (
    <div className="space-y-3">
      {question.options?.map((option, index) => {
        const optionKey = String.fromCharCode(65 + index); // A, B, C, D
        const isSelected = answer === option;
        const isCorrect =
          showCorrectAnswer && option === question.correctAnswer;
        const isIncorrect =
          showCorrectAnswer && isSelected && option !== question.correctAnswer;

        return (
          <label
            key={index}
            className={`flex items-center space-x-3 p-3 rounded-lg border cursor-pointer transition-all ${
              isCorrect
                ? "bg-green-50 border-green-300 text-green-800"
                : isIncorrect
                ? "bg-red-50 border-red-300 text-red-800"
                : isSelected
                ? "bg-blue-50 border-blue-300 text-blue-800"
                : "bg-white border-gray-200 hover:bg-gray-50"
            } ${isReviewMode ? "cursor-default" : ""}`}
          >
            <input
              type="radio"
              name={`question-${question._id}`}
              value={option}
              checked={isSelected}
              onChange={(e) => !isReviewMode && onAnswerChange(e.target.value)}
              disabled={isReviewMode}
              className="sr-only"
            />
            <div
              className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                isCorrect
                  ? "border-green-500 bg-green-500"
                  : isIncorrect
                  ? "border-red-500 bg-red-500"
                  : isSelected
                  ? "border-blue-500 bg-blue-500"
                  : "border-gray-300"
              }`}
            >
              {(isSelected || isCorrect) && (
                <div className="w-2 h-2 bg-white rounded-full"></div>
              )}
            </div>
            <span className="font-medium text-gray-700">{optionKey}.</span>
            <span className="flex-1">{option}</span>
            {isCorrect && <CheckCircle className="w-5 h-5 text-green-600" />}
          </label>
        );
      })}
    </div>
  );

  const renderTrueFalse = () => (
    <div className="space-y-3">
      {["True", "False"].map((option) => {
        const isSelected = answer === option;
        const isCorrect =
          showCorrectAnswer && option === question.correctAnswer;
        const isIncorrect =
          showCorrectAnswer && isSelected && option !== question.correctAnswer;

        return (
          <label
            key={option}
            className={`flex items-center space-x-3 p-3 rounded-lg border cursor-pointer transition-all ${
              isCorrect
                ? "bg-green-50 border-green-300 text-green-800"
                : isIncorrect
                ? "bg-red-50 border-red-300 text-red-800"
                : isSelected
                ? "bg-blue-50 border-blue-300 text-blue-800"
                : "bg-white border-gray-200 hover:bg-gray-50"
            } ${isReviewMode ? "cursor-default" : ""}`}
          >
            <input
              type="radio"
              name={`question-${question._id}`}
              value={option}
              checked={isSelected}
              onChange={(e) => !isReviewMode && onAnswerChange(e.target.value)}
              disabled={isReviewMode}
              className="sr-only"
            />
            <div
              className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                isCorrect
                  ? "border-green-500 bg-green-500"
                  : isIncorrect
                  ? "border-red-500 bg-red-500"
                  : isSelected
                  ? "border-blue-500 bg-blue-500"
                  : "border-gray-300"
              }`}
            >
              {(isSelected || isCorrect) && (
                <div className="w-2 h-2 bg-white rounded-full"></div>
              )}
            </div>
            <span className="flex-1">{option}</span>
            {isCorrect && <CheckCircle className="w-5 h-5 text-green-600" />}
          </label>
        );
      })}
    </div>
  );

  const renderShortAnswer = () => (
    <textarea
      value={answer || ""}
      onChange={(e) => !isReviewMode && onAnswerChange(e.target.value)}
      disabled={isReviewMode}
      placeholder="Enter your answer here..."
      className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
        isReviewMode ? "bg-gray-50" : "bg-white"
      }`}
      rows={4}
    />
  );

  const renderFillInBlank = () => {
    const parts = question.question.split("____");
    const blanks = (answer || []) as string[];

    return (
      <div className="space-y-4">
        <div className="text-gray-800 leading-relaxed">
          {parts.map((part, index) => (
            <React.Fragment key={index}>
              {part}
              {index < parts.length - 1 && (
                <input
                  type="text"
                  value={blanks[index] || ""}
                  onChange={(e) => {
                    if (!isReviewMode) {
                      const newBlanks = [...blanks];
                      newBlanks[index] = e.target.value;
                      onAnswerChange(newBlanks);
                    }
                  }}
                  disabled={isReviewMode}
                  className={`inline-block mx-2 px-2 py-1 border-b-2 border-blue-300 bg-transparent focus:outline-none focus:border-blue-500 min-w-[100px] ${
                    isReviewMode ? "bg-gray-50" : ""
                  }`}
                  placeholder="___"
                />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {question.question}
          </h3>
          {question.points && (
            <p className="text-sm text-gray-600 mb-4">
              Points: {question.points}
            </p>
          )}
        </div>
      </div>

      <div className="mt-4">
        {question.type === "multiple_choice" && renderMultipleChoice()}
        {question.type === "true_false" && renderTrueFalse()}
        {question.type === "short_answer" && renderShortAnswer()}
        {question.type === "essay" && renderShortAnswer()}
        {question.type === "fill_blank" && renderFillInBlank()}
      </div>

      {showCorrectAnswer && question.explanation && (
        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <h4 className="font-medium text-blue-900 mb-1">Explanation:</h4>
          <p className="text-blue-800 text-sm">{question.explanation}</p>
        </div>
      )}
    </div>
  );
};

const QuizInterface: React.FC = () => {
  const { quizId } = useParams<{ quizId: string }>();
  const navigate = useNavigate();

  console.log("🎯 QuizInterface component mounted/updated for quizId:", quizId);

  const [state, setState] = useState<QuizState>({
    quiz: null,
    questions: [],
    currentQuestionIndex: 0,
    answers: {},
    isSubmitted: false,
    attempt: null,
    loading: true,
    error: null,
    flaggedQuestions: new Set(),
  });

  const timerRef = React.useRef<NodeJS.Timeout>();

  // Initialize quiz
  useEffect(() => {
    if (quizId) {
      initializeQuiz();
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [quizId]);

  const initializeQuiz = async () => {
    try {
      setState((prev) => ({ ...prev, loading: true, error: null }));

      // Start quiz attempt
      const response = await apiClient.post(`/elearning/quiz/${quizId}/start`);
      const { quiz, attempt, questions } = response.data;

      setState((prev) => ({
        ...prev,
        quiz,
        questions: quiz.settings?.shuffleQuestions
          ? shuffleArray(questions)
          : questions,
        attempt,
        loading: false,
      }));

      // Start the timer with the quiz time limit (only if no valid stored timer exists)
      if (quiz.timeLimit) {
        // Check if we have any valid stored timer data
        const storedTimerKey = `quiz-timer-${quizId}`;
        const storedTimer = localStorage.getItem(storedTimerKey);

        let shouldStartTimer = true;
        if (storedTimer) {
          try {
            const timerData = JSON.parse(storedTimer);
            // Only consider the timer valid if expectedEndTime exists and is in the future
            if (
              timerData.expectedEndTime &&
              timerData.expectedEndTime > Date.now()
            ) {
              shouldStartTimer = false; // Valid timer exists, don't start a new one
            }
          } catch (error) {
            // Invalid stored data, start a new timer
            shouldStartTimer = true;
          }
        }

        if (shouldStartTimer) {
          // No valid stored timer, start a new one
          startTimer(quiz.timeLimit * 60);
        }
        // If valid timer exists, let the hook handle resuming it
      }
    } catch (error) {
      setState((prev) => ({
        ...prev,
        error: error instanceof Error ? error.message : "Failed to load quiz",
        loading: false,
      }));
    }
  };

  const shuffleArray = <T,>(array: T[]): T[] => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  const handleAnswerChange = useCallback((questionId: string, answer: any) => {
    setState((prev) => ({
      ...prev,
      answers: {
        ...prev.answers,
        [questionId]: answer,
      },
    }));

    // Auto-save answer
    saveAnswer(questionId, answer);
  }, []);

  const saveAnswer = async (questionId: string, answer: any) => {
    try {
      await apiClient.post(`/elearning/quiz/${quizId}/answer`, {
        questionId,
        answer,
        attemptId: state.attempt?._id,
      });
    } catch (error) {
      console.error("Error saving answer:", error);
    }
  };

  const handleTimeUp = useCallback(() => {
    submitQuiz();
  }, []);

  // Initialize timer hook
  const {
    timeRemaining,
    formattedTime,
    isRunning,
    start: startTimer,
    stop: stopTimer,
    isTimeUp,
  } = useQuizTimer(
    0, // Will be set when quiz loads
    handleTimeUp,
    quizId
  );

  const submitQuiz = async () => {
    console.log("🚀 SUBMIT_QUIZ FUNCTION CALLED 🚀");
    console.log("Current quizId:", quizId);

    // Clear the timer immediately, regardless of API success/failure
    console.log("⏰ Clearing timer immediately for quiz:", quizId);
    stopTimer();
    if (quizId) {
      const storageKey = `quiz-timer-${quizId}`;
      console.log("🗑️ Manually clearing localStorage key:", storageKey);
      localStorage.removeItem(storageKey);
    }

    try {
      console.log("⏳ Setting loading state...");
      setState((prev) => ({ ...prev, loading: true }));

      console.log("📡 Submitting quiz to API...", quizId);
      const response = await apiClient.post(
        `/elearning/quiz/${quizId}/submit`,
        {
          answers: state.answers,
          attemptId: state.attempt?._id,
        }
      );

      console.log("✅ Quiz submission response:", response.data);
      setState((prev) => ({
        ...prev,
        isSubmitted: true,
        attempt: response.data.attempt,
        loading: false,
      }));
    } catch (error) {
      console.error("❌ Error submitting quiz:", error);
      setState((prev) => ({
        ...prev,
        error: error instanceof Error ? error.message : "Failed to submit quiz",
        loading: false,
      }));
    }
  };

  const toggleQuestionFlag = (questionId: string) => {
    setState((prev) => {
      const newFlagged = new Set(prev.flaggedQuestions);
      if (newFlagged.has(questionId)) {
        newFlagged.delete(questionId);
      } else {
        newFlagged.add(questionId);
      }
      return {
        ...prev,
        flaggedQuestions: newFlagged,
      };
    });
  };

  const goToQuestion = (index: number) => {
    setState((prev) => ({
      ...prev,
      currentQuestionIndex: Math.max(
        0,
        Math.min(index, prev.questions.length - 1)
      ),
    }));
  };

  const goToPrevious = () => {
    goToQuestion(state.currentQuestionIndex - 1);
  };

  const goToNext = () => {
    goToQuestion(state.currentQuestionIndex + 1);
  };

  const getAnsweredCount = () => {
    return Object.keys(state.answers).length;
  };

  const getCurrentQuestion = () => {
    return state.questions[state.currentQuestionIndex];
  };

  console.log("📊 QuizInterface state:", {
    loading: state.loading,
    error: state.error,
    isSubmitted: state.isSubmitted,
    quiz: !!state.quiz,
    questionsCount: state.questions.length,
    currentQuestionIndex: state.currentQuestionIndex,
  });

  if (state.loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading quiz...</p>
        </div>
      </div>
    );
  }

  if (state.error || !state.quiz) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Error Loading Quiz
          </h2>
          <p className="text-gray-600 mb-4">{state.error}</p>
          <button
            onClick={() => navigate(-1)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (state.isSubmitted) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>

            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Quiz Submitted Successfully!
            </h1>
            <p className="text-gray-600 mb-6">
              Your answers have been saved and the quiz has been submitted for
              grading.
            </p>

            {state.attempt && (
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-sm text-gray-600">Score</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {state.attempt.totalScore || 0} / {state.quiz.totalPoints}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Percentage</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {state.attempt.percentageScore || 0}%
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Time Spent</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {Math.round((state.attempt.timeSpent || 0) / 60)} minutes
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              {state.quiz.settings?.showResults && (
                <button
                  onClick={() => navigate(`/elearning/quiz/${quizId}/results`)}
                  className="flex items-center justify-center space-x-2 bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
                >
                  <Eye className="w-4 h-4" />
                  <span>View Results</span>
                </button>
              )}

              <button
                onClick={() => navigate(-1)}
                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Back to Course
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const currentQuestion = getCurrentQuestion();
  const showAllQuestions = !state.quiz.settings?.oneQuestionAtATime;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <h1 className="text-xl font-semibold text-gray-900">
                {state.quiz.title}
              </h1>
              {!showAllQuestions && (
                <span className="text-sm text-gray-600">
                  Question {state.currentQuestionIndex + 1} of{" "}
                  {state.questions.length}
                </span>
              )}
              {showAllQuestions && (
                <span className="text-sm text-gray-600">
                  {state.questions.length} questions
                </span>
              )}
            </div>

            <div className="flex items-center space-x-4">
              {state.quiz.timeLimit && (
                <QuizTimer
                  timeRemaining={timeRemaining}
                  onTimeUp={handleTimeUp}
                  isActive={isRunning && !state.isSubmitted}
                />
              )}

              <div className="text-sm text-gray-600">
                Answered: {getAnsweredCount()}/{state.questions.length}
              </div>
            </div>
          </div>

          {/* Progress Bar - only show if setting enabled */}
          {state.quiz.settings?.showProgressBar && (
            <div className="mt-3">
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{
                    width: `${
                      (getAnsweredCount() / state.questions.length) * 100
                    }%`,
                  }}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Question Navigation Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sticky top-24">
              <h3 className="font-semibold text-gray-900 mb-4">Questions</h3>

              <div className="grid grid-cols-5 lg:grid-cols-1 gap-2">
                {state.questions.map((question, index) => {
                  const isAnswered = state.answers.hasOwnProperty(question._id);
                  const isCurrent =
                    !showAllQuestions && index === state.currentQuestionIndex;
                  const isFlagged = state.flaggedQuestions.has(question._id);

                  return (
                    <button
                      key={question._id}
                      onClick={() => {
                        if (showAllQuestions) {
                          // Scroll to question
                          document
                            .getElementById(`question-${question._id}`)
                            ?.scrollIntoView({
                              behavior: "smooth",
                              block: "center",
                            });
                        } else {
                          goToQuestion(index);
                        }
                      }}
                      className={`relative p-2 rounded text-sm font-medium transition-colors ${
                        isCurrent
                          ? "bg-blue-600 text-white"
                          : isAnswered
                          ? "bg-green-100 text-green-800 hover:bg-green-200"
                          : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                      }`}
                    >
                      {index + 1}
                      {isFlagged && (
                        <Flag className="absolute -top-1 -right-1 w-3 h-3 text-yellow-500" />
                      )}
                    </button>
                  );
                })}
              </div>

              <div className="mt-6 space-y-2 text-xs text-gray-600">
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-green-100 rounded"></div>
                  <span>Answered</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-gray-100 rounded"></div>
                  <span>Not answered</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Flag className="w-3 h-3 text-yellow-500" />
                  <span>Flagged</span>
                </div>
              </div>
            </div>
          </div>

          {/* Main Question Area */}
          <div className="lg:col-span-3">
            {/* Show All Questions Mode */}
            {showAllQuestions ? (
              <div className="space-y-6">
                {state.questions.map((question, index) => {
                  const isAnswered = state.answers.hasOwnProperty(question._id);
                  const isFlagged = state.flaggedQuestions.has(question._id);

                  return (
                    <div
                      key={question._id}
                      id={`question-${question._id}`}
                      className={`bg-white rounded-lg shadow-sm border ${
                        isFlagged ? "border-yellow-400" : "border-gray-200"
                      } p-6`}
                    >
                      <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center space-x-3">
                          <span
                            className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium ${
                              isAnswered
                                ? "bg-green-100 text-green-800"
                                : "bg-gray-100 text-gray-600"
                            }`}
                          >
                            {index + 1}
                          </span>
                          <h2 className="text-sm font-medium text-gray-600">
                            Question {index + 1} of {state.questions.length}
                          </h2>
                        </div>

                        <button
                          onClick={() => toggleQuestionFlag(question._id)}
                          className={`flex items-center space-x-1 px-2 py-1 text-xs rounded ${
                            isFlagged
                              ? "bg-yellow-100 text-yellow-800"
                              : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                          }`}
                        >
                          <Flag className="w-3 h-3" />
                          <span>{isFlagged ? "Unflag" : "Flag"}</span>
                        </button>
                      </div>

                      <QuestionRenderer
                        question={question}
                        answer={state.answers[question._id]}
                        onAnswerChange={(answer) =>
                          handleAnswerChange(question._id, answer)
                        }
                      />
                    </div>
                  );
                })}

                {/* Submit Button for All Questions Mode */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-600">
                      <span className="font-medium">{getAnsweredCount()}</span>{" "}
                      of{" "}
                      <span className="font-medium">
                        {state.questions.length}
                      </span>{" "}
                      questions answered
                    </div>
                    <button
                      onClick={() => {
                        console.log("🔥 SUBMIT BUTTON CLICKED!");
                        submitQuiz();
                      }}
                      className="flex items-center space-x-2 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                    >
                      <Send className="w-4 h-4" />
                      <span>Submit Quiz</span>
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              /* One Question at a Time Mode */
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                {currentQuestion && (
                  <>
                    <div className="flex items-center justify-between mb-6">
                      <h2 className="text-sm font-medium text-gray-600">
                        Question {state.currentQuestionIndex + 1} of{" "}
                        {state.questions.length}
                      </h2>

                      <button
                        onClick={() => toggleQuestionFlag(currentQuestion._id)}
                        className={`flex items-center space-x-1 px-2 py-1 text-xs rounded ${
                          state.flaggedQuestions.has(currentQuestion._id)
                            ? "bg-yellow-100 text-yellow-800"
                            : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                        }`}
                      >
                        <Flag className="w-3 h-3" />
                        <span>
                          {state.flaggedQuestions.has(currentQuestion._id)
                            ? "Unflag"
                            : "Flag"}
                        </span>
                      </button>
                    </div>

                    <QuestionRenderer
                      question={currentQuestion}
                      answer={state.answers[currentQuestion._id]}
                      onAnswerChange={(answer) =>
                        handleAnswerChange(currentQuestion._id, answer)
                      }
                    />

                    {/* Navigation */}
                    <div className="mt-8 pt-6 border-t border-gray-200">
                      {/* Question Navigation Boxes */}
                      <div className="mb-6">
                        <h3 className="text-sm font-medium text-gray-700 mb-3">
                          Jump to Question:
                        </h3>
                        <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                          {state.questions.map((question, index) => {
                            const isAnswered = state.answers.hasOwnProperty(
                              question._id
                            );
                            const isCurrent =
                              index === state.currentQuestionIndex;
                            const isFlagged = state.flaggedQuestions.has(
                              question._id
                            );

                            return (
                              <button
                                key={question._id}
                                onClick={() => goToQuestion(index)}
                                className={`flex-shrink-0 w-10 h-10 text-sm font-medium rounded border transition-colors ${
                                  isCurrent
                                    ? "bg-blue-600 text-white border-blue-600"
                                    : isAnswered
                                    ? "bg-green-100 text-green-800 border-green-300 hover:bg-green-200"
                                    : "bg-gray-100 text-gray-600 border-gray-300 hover:bg-gray-200"
                                }`}
                                title={`Question ${index + 1}${
                                  isAnswered ? " (Answered)" : " (Not answered)"
                                }${isFlagged ? " (Flagged)" : ""}`}
                              >
                                {index + 1}
                                {isFlagged && (
                                  <Flag className="absolute -top-1 -right-1 w-3 h-3 text-yellow-500" />
                                )}
                              </button>
                            );
                          })}
                        </div>
                        <div className="flex items-center justify-center space-x-6 mt-3 text-xs text-gray-600">
                          <div className="flex items-center space-x-1">
                            <div className="w-3 h-3 bg-green-100 border border-green-300 rounded"></div>
                            <span>Answered</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <div className="w-3 h-3 bg-gray-100 border border-gray-300 rounded"></div>
                            <span>Not answered</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <Flag className="w-3 h-3 text-yellow-500" />
                            <span>Flagged</span>
                          </div>
                        </div>
                      </div>

                      {/* Previous/Next Navigation */}
                      <div className="flex items-center justify-between">
                        <button
                          onClick={goToPrevious}
                          disabled={state.currentQuestionIndex === 0}
                          className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <ChevronLeft className="w-4 h-4" />
                          <span>Previous</span>
                        </button>

                        <div className="flex space-x-3">
                          <button
                            onClick={() => {
                              console.log("🔥 SUBMIT BUTTON CLICKED!");
                              submitQuiz();
                            }}
                            className="flex items-center space-x-2 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                          >
                            <Send className="w-4 h-4" />
                            <span>Submit Quiz</span>
                          </button>
                        </div>

                        <button
                          onClick={goToNext}
                          disabled={
                            state.currentQuestionIndex ===
                            state.questions.length - 1
                          }
                          className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <span>Next</span>
                          <ChevronRight className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuizInterface;
